import { atom } from 'jotai';

type LocationState = {
  pathname: string;
  searchParams: URLSearchParams;
  hash: string;
};

// Plain atom updated by useSyncLocationAtom (src/hooks/useSyncLocationAtom.ts), which bridges
// React Router's useLocation() into Jotai. Must be synced at the app root level (inside both
// JotaiProvider and a Router) so all derived atoms stay in sync with navigation.
export const locationAtom = atom<LocationState>({
  pathname: window.location.pathname,
  searchParams: new URLSearchParams(window.location.search),
  hash: window.location.hash,
});

export const syncLocationAtom = atom(null, (_get, set, location: LocationState) => {
  set(locationAtom, location);
});

type ParamType = 'string' | 'number' | 'boolean';

type ParsedType<T extends ParamType> = T extends 'number' ? number : T extends 'boolean' ? boolean : string;

// Creates a read-only atom derived from a URL search parameter. Returns null when the param is absent.
// The optional `type` argument ('string' | 'number' | 'boolean') coerces the raw string value and
// narrows the TypeScript return type accordingly.
export const atomWithQueryParam = <T extends ParamType = 'string'>(key: string, type?: T) =>
  atom((get): ParsedType<T> | null => {
    const raw = get(locationAtom).searchParams.get(key);
    if (raw === null) return null;
    if (type === 'number') return Number(raw) as ParsedType<T>;
    if (type === 'boolean') return (raw === 'true') as ParsedType<T>;
    return raw as ParsedType<T>;
  });
